import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { SettingsForm } from './SettingsForm';
import { StoreLocationsSettings } from './StoreLocationsSettings';
import { ShiftSettingsForm } from './ShiftSettingsForm';
import { StaffManagementForm } from './StaffManagementForm';
import { RolePercentageConfig } from './RolePercentageConfig';
import { OnboardingForm } from './OnboardingForm';
import { BillingSettings } from './BillingSettings';
import { TipFlowGuide } from './TipFlowGuide';
import { ConfigurationRequestForm } from './ConfigurationRequestForm';
import { ReferralProgram } from './ReferralProgram';
import { Sliders, CreditCard, LifeBuoy } from 'lucide-react';

interface Props {
  tenantId: string;
}

type TabGroup = 'operations' | 'billing' | 'support';

interface TabItem {
  id: string;
  label: string;
  group: TabGroup;
  component: React.ReactNode;
}

export const SettingsTabs: React.FC<Props> = ({ tenantId }) => {
  const { tab } = useParams();
  const navigate = useNavigate();
  const activeTab = tab || 'general';

  const getInitialSubTab = (currentTab: string): 'pos' | 'payroll' | 'compliance' | 'fees' => {
    if (currentTab === 'payroll-settings' || currentTab === 'payroll-config') return 'payroll';
    if (currentTab === 'compliance' || currentTab === 'compliance-8027' || currentTab === 'irs-8027') return 'compliance';
    if (currentTab === 'service-fees' || currentTab === 'fees' || currentTab === 'merchant-fees') return 'fees';
    return 'pos';
  };

  const tabs: TabItem[] = [
    // Group 1: Operations & Setup
    { id: 'general', label: 'General & POS', group: 'operations', component: <SettingsForm tenantId={tenantId} initialSubTab={getInitialSubTab(activeTab)} /> },
    { id: 'locations', label: 'Store Locations', group: 'operations', component: <StoreLocationsSettings tenantId={tenantId} /> },
    { id: 'shifts', label: 'Shifts & Hours', group: 'operations', component: <ShiftSettingsForm tenantId={tenantId} /> },
    { id: 'allocations', label: 'Tip Allocations', group: 'operations', component: <RolePercentageConfig tenantId={tenantId} /> },
    { id: 'onboarding', label: 'Business Profile', group: 'operations', component: <OnboardingForm tenantId={tenantId} /> },

    // Group 2: Billing & Growth
    { id: 'billing', label: 'Subscription & Billing', group: 'billing', component: <BillingSettings tenantId={tenantId} /> },
    { id: 'referral', label: 'Referral Program (10%)', group: 'billing', component: <ReferralProgram tenantId={tenantId} /> },

    // Group 3: Support & Knowledge
    { id: 'guide', label: 'TipFlow Guide', group: 'support', component: <TipFlowGuide /> },
    { id: 'requests', label: 'Contact Support', group: 'support', component: <ConfigurationRequestForm /> },
  ];

  // Find the group of the active tab, defaulting to 'operations'
  const currentTabObj = tabs.find(t => t.id === activeTab);
  const initialGroup: TabGroup = currentTabObj ? currentTabObj.group : 'operations';
  const [selectedGroup, setSelectedGroup] = useState<TabGroup>(initialGroup);

  // Synchronize group when URL tab changes
  useEffect(() => {
    if (currentTabObj && currentTabObj.group !== selectedGroup) {
      setSelectedGroup(currentTabObj.group);
    }
  }, [activeTab]);

  const handleGroupChange = (group: TabGroup) => {
    setSelectedGroup(group);
    const firstTabInGroup = tabs.find(t => t.group === group);
    if (firstTabInGroup && firstTabInGroup.id !== activeTab) {
      navigate(`/settings/${firstTabInGroup.id}`);
    }
  };

  const groupCategories = [
    {
      id: 'operations' as TabGroup,
      label: 'Operations & Setup',
      icon: Sliders,
      description: 'POS, shifts, staff roster & allocation percentages',
    },
    {
      id: 'billing' as TabGroup,
      label: 'Billing & Growth',
      icon: CreditCard,
      description: 'Subscription plans, invoices & referral rewards',
    },
    {
      id: 'support' as TabGroup,
      label: 'Support & Docs',
      icon: LifeBuoy,
      description: 'TipFlow guide, knowledge base & support tickets',
    },
  ];

  const visibleSubTabs = tabs.filter(t => t.group === selectedGroup);

  return (
    <div className="font-sans space-y-6">
      {/* Top Level Category Selector */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {groupCategories.map(cat => {
          const Icon = cat.icon;
          const isSelected = selectedGroup === cat.id;
          return (
            <button
              key={cat.id}
              onClick={() => handleGroupChange(cat.id)}
              className={`p-4 rounded-2xl border text-left transition-all cursor-pointer flex items-start gap-3.5 ${
                isSelected
                  ? 'bg-slate-900 border-slate-900 text-white shadow-md'
                  : 'bg-white border-gray-200 text-gray-700 hover:border-gray-300 hover:bg-gray-50/70'
              }`}
            >
              <div
                className={`p-2.5 rounded-xl ${
                  isSelected ? 'bg-white/10 text-white' : 'bg-slate-100 text-slate-700'
                }`}
              >
                <Icon className="w-5 h-5" />
              </div>
              <div className="min-w-0 flex-1">
                <span
                  className={`block text-xs font-black uppercase tracking-wider ${
                    isSelected ? 'text-white' : 'text-slate-900'
                  }`}
                >
                  {cat.label}
                </span>
                <span
                  className={`block text-[11px] font-medium truncate mt-0.5 ${
                    isSelected ? 'text-slate-300' : 'text-gray-500'
                  }`}
                >
                  {cat.description}
                </span>
              </div>
            </button>
          );
        })}
      </div>

      {/* Sub-Tabs Navigation for the Active Category */}
      <div className="flex border-b border-gray-200 overflow-x-auto no-scrollbar -mx-4 px-4 md:mx-0 md:px-0 scroll-smooth">
        <div className="flex space-x-2 min-w-max pr-8 md:pr-0">
          {visibleSubTabs.map(subTab => (
            <button
              key={subTab.id}
              onClick={() => navigate(`/settings/${subTab.id}`)}
              className={`px-5 py-3.5 font-bold uppercase tracking-wider text-[11px] md:text-xs whitespace-nowrap transition-all relative tap-highlight-transparent cursor-pointer ${
                activeTab === subTab.id
                  ? 'text-blue-600 font-black'
                  : 'text-gray-500 hover:text-gray-800'
              }`}
            >
              {subTab.label}
              {activeTab === subTab.id && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 rounded-full" />
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Active Tab Content Panel */}
      <div className="bg-white p-3 md:p-6 rounded-2xl shadow-sm border border-gray-200">
        {tabs.find(t => t.id === activeTab)?.component || visibleSubTabs[0]?.component || tabs[0].component}
      </div>
    </div>
  );
};

